<script setup>
// Uma resposta da conversa de teste (L-18): o texto pelo MessageFormatter, a hora e o botão de
// copiar. Serve à parte da resposta do motor e à mensagem que chega no canal, do agente ou da pessoa
// da equipe. Copia o texto como ele chegou, com a formatação que o agente escreveu. "Copiado" aparece
// por dois segundos; quando o navegador não deixa copiar (permissão negada, página sem foco), o
// botão diz que não deu, em vez de fingir que copiou.
import { onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { horaDe } from './hora';

const props = defineProps({
  texto: { type: String, required: true },
  quando: { type: String, default: '' },
});

const { t, locale } = useI18n();
const { formatMessage } = useMessageFormatter();

// '' | 'copiado' | 'falhou'
const copia = ref('');
let relogio = null;

async function copiar() {
  clearTimeout(relogio);
  try {
    await copyTextToClipboard(props.texto);
    copia.value = 'copiado';
  } catch (e) {
    copia.value = 'falhou';
  }
  relogio = setTimeout(() => {
    copia.value = '';
  }, 2000);
}

onBeforeUnmount(() => clearTimeout(relogio));
</script>

<template>
  <div
    class="max-w-[85%] px-3.5 py-2 text-sm break-words bg-n-solid-iris text-n-slate-12 rounded-2xl rounded-bl-md"
  >
    <span
      v-dompurify-html="formatMessage(texto, false, false, true)"
      class="[&_p]:m-0 [&_p+p]:mt-2 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:my-0.5 [&_a]:underline [&_strong]:font-semibold"
    />
    <span
      class="flex items-center justify-end gap-2 mt-1 text-[11px] text-n-slate-10"
    >
      <button
        type="button"
        class="flex items-center gap-1 hover:text-n-slate-12"
        :title="t('CONVERSA_DE_TESTE.COPY')"
        :aria-label="t('CONVERSA_DE_TESTE.COPY')"
        data-copiar
        @click="copiar"
      >
        <i
          class="size-3.5"
          :class="copia === 'copiado' ? 'i-lucide-check' : 'i-lucide-copy'"
        />
        <span v-if="copia" role="status">
          {{
            copia === 'copiado'
              ? t('CONVERSA_DE_TESTE.COPIED')
              : t('CONVERSA_DE_TESTE.COPY_FAILED')
          }}
        </span>
      </button>
      <time v-if="quando" :datetime="quando" data-hora>
        {{ horaDe(quando, locale) }}
      </time>
    </span>
  </div>
</template>
